import React, { useEffect, useState, useRef } from "react"
import { useTranslation } from "react-i18next"

import CenteredFallback from "../components/CenteredFallback/CenteredFallback"
import Loader from "../components/Loader/Loader"
import views from "../data/all-views.json"
import {
  getDataConfigsUrl,
  getWantedSite,
  getDefaultLanguage,
  getEnabledLanguages,
} from "../helpers/general"
import { setSiteConfig } from "../hooks/use-site-config"
import locales from "../locales"

export const AppContext = React.createContext(null)

const enabledLanguages = getEnabledLanguages(locales)
const defaultLanguage = getDefaultLanguage(locales)

const dataUrl = getDataConfigsUrl()

// Do cache busting of config files every time the site gets loaded
const cacheBustVersion = `?v=${Date.now()}`

let configFiles = {}

export const ContextWrapper = props => {
  const initialState = initState()

  const [store, setStore] = useState(initialState)

  const [ready, setReady] = useState(false)

  const formikRef = useRef()

  //Get default locale if need redirect
  const { i18n } = useTranslation()
  const localeCodes = Object.keys(enabledLanguages)
  const locale = localeCodes.includes(i18n.language)
    ? i18n.language
    : defaultLanguage

  const wantedData = getWantedSite()
  const wantedSite = wantedData.wantedSite
  const needRedirect = wantedData.redirect

  useEffect(() => {
    fetchIndex().then(data => {
      let site = null
      data.forEach(element => {
        if (element.code === wantedSite.toLowerCase()) {
          site = element
        }
      })

      //If wanted site is not valid site, set site = ingeteamev
      if (!site) {
        site = data.find(item => item.code === "ingeteamev")
      }

      setSiteConfig(site)

      setStore(prevStore => {
        return { ...prevStore, ...{ initConfig: site } }
      })

      fetchSectorsAndChargers().then(data => {
        setStore(prevStore => {
          return {
            ...prevStore,
            ...{ sectors: data.sectors, chargers: data.chargers },
          }
        })

        fetchConfigs(data.chargers, site).then(data => {
          Object.entries(data).map(async ([key, charger]) => {
            const processedCategories = processCategories(charger)
            data[key].categories = processedCategories
          })

          const processedData = { ...data }

          setStore(prevStore => {
            return { ...prevStore, ...processedData }
          })

          if (needRedirect) {
            //Redirect
            const basePath = (process.env.PUBLIC_URL || "").replace(/\/+$/, "")
            window.location.href = `${basePath}/${locale}/${site.url}`
          }
          setReady(true)
        })
      })
    })
  }, [locale, needRedirect, wantedSite])

  if (!ready)
    return (
      <CenteredFallback>
        <Loader />
      </CenteredFallback>
    )

  return (
    <AppContext.Provider value={{ store, setStore, formikRef }}>
      {props.children}
    </AppContext.Provider>
  )
}

const initState = () => {
  let obj = {}

  for (const property in configFiles) {
    obj[property] = null
  }

  // Load views from imported local file
  obj["views"] = views

  obj["selectedSector"] = null

  obj["categories"] = null
  obj["incompatibilities"] = null
  obj["variables"] = null
  obj["predesigns"] = null
  obj["defaults"] = null

  return obj
}

// Get variables for each category
const processCategories = data => {
  if (!data || !data.categories) {
    return null
  }

  const processed = Object.entries(data.categories).map(category =>
    processCategory(category, data.variables)
  )

  return Object.fromEntries(processed)
}

const processCategory = (category, allVariables) => {
  const [categoryId, categoryData] = category

  if (Object.entries(categoryData.HIJOS).length <= 0) {
    const variables = getCategoryVariables(category, allVariables)

    const processedCategory = [
      categoryId,
      { ...categoryData, VARIABLES: variables },
    ]

    return processedCategory
  }

  const processedHijos = Object.entries(categoryData.HIJOS).map(childCategory =>
    processCategory(childCategory, allVariables)
  )

  const processedCategory = [
    categoryId,
    { ...categoryData, HIJOS: Object.fromEntries(processedHijos) },
  ]

  return processedCategory
}

const getCategoryVariables = (category, allVariables) => {
  const [categoryId] = category

  const variables = Object.entries(allVariables).filter(variable => {
    const [, variableData] = variable

    return variableData.CATEGORIA === categoryId
  })

  return Object.fromEntries(variables)
}

const fetchIndex = async () => {
  const response = await fetch(`${dataUrl}/index.json`)
  const data = await response.json()
  return data
}

const fetchSectorsAndChargers = async () => {
  const files = {
    sectors: "/sectors-index.json",
    chargers: "/chargers-index.json",
  }

  const allData = await Promise.all(
    Object.keys(files).map(async key => {
      const fileName = files[key]
      const url = `${dataUrl}${fileName}`

      const response = await fetch(url)
      const data = await response.json()

      return { [key]: data }
    })
  )

  return allData.reduce((accumulator, currentValue) => {
    return { ...accumulator, ...currentValue }
  })
}

const fetchConfigs = async (chargers, site) => {
  const resultsArray = await Promise.all(
    chargers.map(async charger => {
      const configFiles = {
        categories:
          site.configurationFilesPath +
          `/${charger.code}/category-tree.json` +
          cacheBustVersion,
        variables:
          site.configurationFilesPath +
          `/${charger.code}/all-variables.json` +
          cacheBustVersion,
        incompatibilities:
          site.configurationFilesPath +
          `/${charger.code}/no-compatibilities.json` +
          cacheBustVersion,
        predesigns:
          site.configurationFilesPath +
          `/${charger.code}/pre-designs.json` +
          cacheBustVersion,
        defaults:
          site.configurationFilesPath +
          `/${charger.code}/default-values.json` +
          cacheBustVersion,
      }

      const allData = await Promise.all(
        Object.entries(configFiles).map(async ([key, fileName]) => {
          const url = `${dataUrl}${fileName}`
          const response = await fetch(url)
          const data = await response.json()
          return { [key]: data }
        })
      )

      const mergedData = allData.reduce(
        (acc, curr) => ({ ...acc, ...curr }),
        {}
      )
      return { [charger.code]: mergedData }
    })
  )

  const finalResult = resultsArray.reduce(
    (acc, curr) => ({ ...acc, ...curr }),
    {}
  )

  return finalResult
}
